import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { peopleAPI, familiesAPI } from '../services/api';
import GlassCard from '../components/GlassCard';
import SearchablePersonSelect from '../components/SearchablePersonSelect';
import FamilyTreeComponent from '../d3/charts/FamilyTreeComponent';

/**
 * Find the root grandfather for the tree
 * 1. Try father's father (paternal grandfather)
 * 2. If not found, try mother's father (maternal grandfather)
 * 3. If no grandfather found, use highest ancestor available
 */
const findRootGrandfather = async (personData, selectedPersonData, peopleAPI) => {
  
  // Get parents
  const parents = personData.parents || [];
  const father = parents.find(p => p.sex === 'male');
  const mother = parents.find(p => p.sex === 'female');

  // Try to find paternal grandfather (father's father)
  if (father) {
    try {
      const fatherResponse = await peopleAPI.getWithRelationships(father.id || father.person_id);
      const fatherData = fatherResponse.data?.person_data || fatherResponse.data;
      const fatherParents = fatherData?.parents || [];
      const paternalGrandfather = fatherParents.find(p => p.sex === 'male');
      if (paternalGrandfather) {
        return { 
          person: paternalGrandfather, 
          type: 'paternal-grandfather',
          level: 2 
        };
      }
    } catch (err) {
      console.error('Error fetching father relationships:', err);
    }
  }

  // Try to find maternal grandfather (mother's father)
  if (mother) {
    try {
      const motherResponse = await peopleAPI.getWithRelationships(mother.id || mother.person_id);
      const motherData = motherResponse.data?.person_data || motherResponse.data;
      const motherParents = motherData?.parents || [];
      const maternalGrandfather = motherParents.find(p => p.sex === 'male');
      if (maternalGrandfather) {
        return { 
          person: maternalGrandfather, 
          type: 'maternal-grandfather',
          level: 2 
        };
      }
    } catch (err) {
      console.error('Error fetching mother relationships:', err);
    }
  }

  // If no grandfather found, use highest ancestor available
  // Try grandparents from extended_parents
  const grandparents = selectedPersonData.grandparents || [];
  if (grandparents.length > 0) {
    const grandfather = grandparents.find(gp => gp.sex === 'male') || grandparents[0];
    return { 
      person: grandfather, 
      type: 'highest-ancestor',
      level: grandparents.find(gp => gp.sex === 'male') ? 2 : 1
    };
  }

  // Try parents as fallback
  if (parents.length > 0) {
    const parent = father || parents[0];
    return { 
      person: parent, 
      type: 'parent-fallback',
      level: 1 
    };
  }

  // If nothing found, use selected person
  return { 
    person: personData, 
    type: 'self',
    level: 0 
  };
};

/**
 * Recursively fetch children for a person (including spouse's children)
 * Returns children up to the specified depth
 */
const fetchChildrenRecursive = async (person, currentLevel, maxLevel, peopleAPI, visited = new Set(), parentId = null) => {
  if (currentLevel >= maxLevel) {
    return [];
  }
  const personId = person.id || person.person_id;
  
  // Avoid infinite loops but allow if it's a different parent-child path
  // Only skip if we've already processed this person at the same level with same parent
  const visitKey = `${personId}-${currentLevel}-${parentId}`;
  if (visited.has(visitKey)) {
    return [];
  }
  visited.add(visitKey);

  const allChildren = [];

  try {
    // Fetch person's relationships
    const response = await peopleAPI.getWithRelationships(personId);
    const personData = response.data?.person_data || response.data;
    
    // Get direct children
    const directChildren = personData?.children || [];
    
    // Get spouses
    const spouses = personData?.spouses || [];
    
    // Get children of spouses (spouse's children)
    const spouseChildrenPromises = spouses.map(async (spouse) => {
      try {
        const spouseResponse = await peopleAPI.getWithRelationships(spouse.id || spouse.person_id);
        const spouseData = spouseResponse.data?.person_data || spouseResponse.data;
        return spouseData?.children || [];
      } catch (err) {
        console.error(`Error fetching spouse ${spouse.id} children:`, err);
        return [];
      }
    });
    
    const spouseChildrenArrays = await Promise.all(spouseChildrenPromises);
    const spouseChildren = spouseChildrenArrays.flat();

    // Combine all children and remove duplicates
    const combinedChildren = [...directChildren, ...spouseChildren];
    const uniqueChildren = combinedChildren.filter((child, index, self) => 
      index === self.findIndex(c => (c.id || c.person_id) === (child.id || child.person_id))
    );

    // Process each child recursively
    // We need to fetch each child's relationships to get their spouse information
    for (const child of uniqueChildren) {
      const childId = child.id || child.person_id;
      let childWithSpouse = { ...child, level: currentLevel + 1, children: [] };
      
      // Fetch child's relationships to get spouse information
      try {
        const childResponse = await peopleAPI.getWithRelationships(childId);
        const childData = childResponse.data?.person_data || childResponse.data;
        // Store spouse information with the child
        childWithSpouse.spouseData = childData?.spouses || [];
      } catch (err) {
        console.error(`Error fetching relationships for child ${childId}:`, err);
      }
      
      allChildren.push(childWithSpouse);

      // Recursively fetch grandchildren
      if (currentLevel + 1 < maxLevel) {
        const grandchildren = await fetchChildrenRecursive(child, currentLevel + 1, maxLevel, peopleAPI, visited, personId);
        allChildren[allChildren.length - 1].children = grandchildren;
      }
    }
  } catch (err) {
    console.error(`Error fetching children for person ${personId}:`, err);
  }

  visited.delete(visitKey); // Remove from visited after processing
  return allChildren;
};

/**
 * Build family tree hierarchy starting from root grandfather
 * Structure: Root -> Children -> Grandchildren -> ... (5 levels)
 */
const buildFamilyTree = async (selectedPersonData, peopleAPI) => {
  // Handle both direct person_data and wrapped response
  const personData = selectedPersonData.person_data || selectedPersonData;
  if (!personData) {
    return { tree: null, rootInfo: null };
  }

  const selectedPersonId = personData.id || personData.person_id;
  if (!selectedPersonId) {
    return { tree: null, rootInfo: null };
  }

  // Helper to get person display name
  const getPersonName = (person) => {
    return person?.preferred_name || person?.first_name || 'Unknown';
  };

  // Helper to create node object with spouse information
  const createNode = (person, relationship = null, spouseData = null) => {
    const node = {
      name: getPersonName(person),
      first_name: person?.first_name,
      last_name: person?.last_name,
      preferred_name: person?.preferred_name,
      id: person?.id || person?.person_id,
      person_id: person?.id || person?.person_id,
      sex: person?.sex,
      date_of_birth: person?.date_of_birth,
      picture_url: person?.picture_url,
      relationship: relationship,
      spouse: null,
      children: []
    };

    // Use spouse data if provided (from the relationships view)
    if (spouseData) {
      // If spouseData is an array, take the first spouse (primary spouse)
      const spouse = Array.isArray(spouseData) ? spouseData[0] : spouseData;
      if (spouse) {
        node.spouse = {
          name: getPersonName(spouse),
          first_name: spouse?.first_name,
          last_name: spouse?.last_name,
          preferred_name: spouse?.preferred_name,
          id: spouse?.id || spouse?.person_id,
          person_id: spouse?.id || spouse?.person_id,
          sex: spouse?.sex,
          date_of_birth: spouse?.date_of_birth,
          picture_url: spouse?.picture_url,
          city: spouse?.city,
          country: spouse?.country,
          date_of_death: spouse?.date_of_death
        };
      }
    }

    return node;
  };

  // Find the root grandfather
  const rootInfo = await findRootGrandfather(personData, selectedPersonData, peopleAPI);
  const rootPerson = rootInfo.person;

  // Fetch root person's relationships to get spouse information
  let rootSpouseData = null;
  try {
    const rootId = rootPerson.id || rootPerson.person_id;
    if (rootId) {
      const rootResponse = await peopleAPI.getWithRelationships(rootId);
      const rootPersonData = rootResponse.data?.person_data || rootResponse.data;
      rootSpouseData = rootPersonData?.spouses || [];
    }
  } catch (err) {
    console.error(`Error fetching root person relationships:`, err);
  }

  // Create root node with spouse information from the relationships view
  const rootNode = createNode(rootPerson, rootInfo.type, rootSpouseData);

  // Fetch children recursively for 5 levels (starting from level 0)
  const maxLevels = 5;
  rootNode.children = await fetchChildrenRecursive(rootPerson, 0, maxLevels, peopleAPI);

  // Convert the fetched children structure to D3 nodes with spouse information
  const convertToD3Node = (personData) => {
    // Use spouseData if it was stored during fetchChildrenRecursive
    const spouseData = personData.spouseData || null;
    const node = createNode(personData, personData.relationship || 'descendant', spouseData);
    if (personData.children && personData.children.length > 0) {
      node.children = personData.children.map(convertToD3Node);
    }
    return node;
  };

  rootNode.children = rootNode.children.map(convertToD3Node);

  return { tree: rootNode, rootInfo };
};

const D3TreePage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [families, setFamilies] = useState([]);
  const [people, setPeople] = useState([]);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [selectedPersonData, setSelectedPersonData] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rootPerson, setRootPerson] = useState(null); // Track which grandfather/root was selected
  const [selectedNodeData, setSelectedNodeData] = useState(null); // Track clicked node for popup
  const [activeTab, setActiveTab] = useState('person'); // Track active tab in modal: 'person' or 'spouse'

  // Load families and people
  useEffect(() => {
    loadFamilies();
  }, [user]);

  // Load people when family is selected
  useEffect(() => {
    if (families.length > 0) {
      // Load people from all families
      loadAllPeople();
    }
  }, [families]);

  const loadAllPeople = async () => {
    if (families.length === 0) {
      setPeople([]);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const allPeople = [];
      // Try to load people from each family
      for (const family of families) {
        try {
          const response = await peopleAPI.list(family.id);
          // API returns { people: [...] } format
          const peopleData = response.data?.people || response.data || [];
          if (Array.isArray(peopleData)) {
            allPeople.push(...peopleData);
          }
        } catch (err) {
          console.error(`Error loading people from family ${family.id}:`, err);
          // Continue loading other families even if one fails
        }
      }
      console.log(`Loaded ${allPeople.length} people from ${families.length} families`);
      setPeople(allPeople);
      if (allPeople.length === 0 && families.length > 0) {
        setError('No people found. Please add family members first.');
      }
    } catch (err) {
      console.error('Error loading all people:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load people');
      setPeople([]);
    } finally {
      setLoading(false);
    }
  };

  // Build tree when person data changes
  useEffect(() => {
    const buildTree = async () => {
      if (selectedPersonData) {
        try {
          setLoading(true);
          setError(null);
          const { tree, rootInfo } = await buildFamilyTree(selectedPersonData, peopleAPI);
          setTreeData(tree);
          setRootPerson(rootInfo);
        } catch (err) {
          console.error('Error building tree:', err);
          setError('Failed to build family tree: ' + err.message);
          setTreeData(null);
          setRootPerson(null);
        } finally {
          setLoading(false);
        }
      } else {
        setTreeData(null);
        setRootPerson(null);
      }
    };
    
    buildTree();
  }, [selectedPersonData]);

  const loadFamilies = async () => {
    try {
      const response = await familiesAPI.list();
      // API returns { families: [...] } format
      const familiesData = response.data?.families || response.data || [];
      const familiesArray = Array.isArray(familiesData) ? familiesData : [];
      setFamilies(familiesArray);
      console.log(`Loaded ${familiesArray.length} families`);
    } catch (err) {
      console.error('Error loading families:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load families');
      setFamilies([]);
    }
  };

  // Removed - using loadAllPeople instead

  const handlePersonSelect = async (personId) => {
    if (!personId) {
      setSelectedPersonId(null);
      setSelectedPersonData(null);
      setTreeData(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSelectedPersonId(personId);
      
      // Fetch person with all relationships
      const response = await peopleAPI.getWithRelationships(personId);
      const personData = response.data;
      setSelectedPersonData(personData);

      // Build tree with grandparents
      // First, try to get grandparents by fetching parents' relationships
      const personDataObj = personData.person_data || personData;
      const parents = personDataObj?.parents || [];
      const grandparentsPromises = parents.map(async (parent) => {
        try {
          const parentId = parent.id || parent.person_id;
          if (!parentId) return [];
          const parentResponse = await peopleAPI.getWithRelationships(parentId);
          const parentData = parentResponse.data?.person_data || parentResponse.data;
          return parentData?.parents || [];
        } catch (err) {
          console.error(`Error fetching parent ${parent.id} relationships:`, err);
          return [];
        }
      });

      const grandparentsArrays = await Promise.all(grandparentsPromises);
      const allGrandparents = grandparentsArrays.flat().filter(gp => gp && (gp.id || gp.person_id));

      // Add grandparents to the response data for tree building
      if (!personData.grandparents) {
        personData.grandparents = allGrandparents;
      }

      setSelectedPersonData(personData);
    } catch (err) {
      console.error('Error loading person data:', err);
      setError(err.message || 'Failed to load person data');
      setSelectedPersonId(null);
      setSelectedPersonData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%', padding: '0 16px' }} className="d3-tree-page">
      <GlassCard style={{ marginBottom: '24px', padding: 'clamp(16px, 4vw, 24px)' }}>
        <h2 style={{ 
          marginTop: 0, 
          marginBottom: '12px', 
          color: theme.text.primary, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          fontSize: 'clamp(20px, 4vw, 24px)',
          flexWrap: 'wrap',
        }}>
          <img
            src="/assets/family-tree-logo.png"
            alt="Family Tree"
            style={{
              height: 'clamp(24px, 5vw, 32px)',
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          D3 Family Tree
        </h2>
        <p style={{ 
          margin: 0, 
          color: theme.text.secondary, 
          fontSize: 'clamp(13px, 2.5vw, 14px)', 
          marginBottom: '20px',
          lineHeight: '1.6',
        }}>
          Select a person to visualize their family tree. The tree starts from the highest ancestor (preferably paternal grandfather) and extends down 5 levels, including children of spouses.
        </p>

        {/* Root Person Info */}
        {rootPerson && (
          <div style={{
            padding: 'clamp(10px, 2.5vw, 12px)',
            borderRadius: '6px',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            color: '#16A34A',
            marginBottom: '16px',
            fontSize: 'clamp(12px, 2.5vw, 14px)',
            lineHeight: '1.5',
            wordBreak: 'break-word',
          }}>
            <strong>Tree Root:</strong> {
              rootPerson.type === 'paternal-grandfather' ? 'Paternal Grandfather (Father\'s Father)' :
              rootPerson.type === 'maternal-grandfather' ? 'Maternal Grandfather (Mother\'s Father)' :
              rootPerson.type === 'highest-ancestor' ? 'Highest Ancestor Available' :
              rootPerson.type === 'parent-fallback' ? 'Parent (No Grandfather Found)' :
              'Selected Person (No Ancestors Found)'
            } - {rootPerson.person?.preferred_name || rootPerson.person?.first_name} {rootPerson.person?.last_name || ''}
          </div>
        )}

        {/* Person Selector */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            color: theme.text.primary, 
            fontWeight: 500,
            fontSize: 'clamp(14px, 3vw, 16px)',
          }}>
            Select Person: {people.length > 0 && (
              <span style={{ 
                fontSize: 'clamp(11px, 2.5vw, 12px)', 
                fontWeight: 'normal', 
                color: theme.text.secondary 
              }}>
                ({people.length} available)
              </span>
            )}
          </label>
          {loading && (
            <div style={{
              padding: 'clamp(10px, 2.5vw, 12px)',
              borderRadius: '6px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              color: '#2563EB',
              marginBottom: '8px',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
            }}>
              Loading people... Please wait.
            </div>
          )}
          {!loading && people.length === 0 && families.length > 0 && (
            <div style={{
              padding: 'clamp(10px, 2.5vw, 12px)',
              borderRadius: '6px',
              background: 'rgba(234, 179, 8, 0.1)',
              border: '1px solid rgba(234, 179, 8, 0.3)',
              color: '#CA8A04',
              marginBottom: '8px',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
            }}>
              No people found in your families. Please add family members first.
            </div>
          )}
          <SearchablePersonSelect
            value={selectedPersonId}
            onChange={handlePersonSelect}
            options={people || []}
            placeholder={people.length > 0 ? "Type to search for a person..." : "Loading people..."}
          />
        </div>

        {error && (
          <div style={{
            padding: 'clamp(10px, 2.5vw, 12px)',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#DC2626',
            marginBottom: '16px',
            fontSize: 'clamp(13px, 2.5vw, 14px)',
            wordBreak: 'break-word',
          }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: theme.text.secondary,
          }}>
            Loading...
          </div>
        )}
      </GlassCard>

      {/* Tree Visualization */}
      {treeData && !loading && rootPerson && (
        <GlassCard style={{ 
          height: 'calc(100vh - 280px)',
          minHeight: '400px',
          marginBottom: '24px',
          padding: 'clamp(12px, 3vw, 20px)',
        }}
        className="tree-visualization-card"
        >
          <div style={{
            marginBottom: '8px',
            fontSize: 'clamp(11px, 2.5vw, 12px)',
            color: theme.text.secondary,
            textAlign: 'center',
          }}>
            💡 Use mouse wheel to zoom, drag to pan (on mobile: pinch to zoom, drag to pan)
          </div>
          <div 
            id="tree-container"
            style={{
              width: '100%',
              height: 'calc(100% - 24px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              position: 'relative',
              touchAction: 'pan-x pan-y pinch-zoom',
            }}
          >
            <FamilyTreeComponent
              data={treeData}
              label={(d) => {
                // Get name (no gender symbol - node color indicates gender)
                const personName = d.name || d.preferred_name || d.first_name || 'Unknown';
                // Include spouse name in parentheses if spouse exists
                if (d.spouse && (d.spouse.name || d.spouse.first_name || d.spouse.preferred_name)) {
                  const spouseName = d.spouse.preferred_name || d.spouse.first_name || d.spouse.name;
                  return `${personName} (${spouseName})`;
                }
                return personName;
              }}
              title={(d) => {
                const name = `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.name || 'Unknown';
                const relationship = d.relationship ? ` (${d.relationship})` : '';
                const spouseInfo = d.spouse ? ` - Has spouse: ${d.spouse.preferred_name || d.spouse.first_name || d.spouse.name}` : '';
                return `${name}${relationship}${spouseInfo}`;
              }}
              width={null}
              height={null}
              margin={100}
              selectedPersonId={selectedPersonId}
              textColor={theme.text.primary}
              selectedTextColor={theme.accent.primary || "#F59E0B"}
              onNodeClick={(nodeData) => {
                setSelectedNodeData(nodeData);
                setActiveTab('person'); // Reset to person tab when node is clicked
              }}
            />
          </div>
        </GlassCard>
      )}

      {/* Person Details Modal with Tabs */}
      {selectedNodeData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '16px',
            overflowY: 'auto',
          }}
          onClick={() => {
            setSelectedNodeData(null);
            setActiveTab('person');
          }}
        >
          <GlassCard
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '95vh',
              overflowY: 'auto',
              margin: 'auto',
              padding: 'clamp(16px, 4vw, 24px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '12px',
              justifyContent: 'space-between', 
              alignItems: 'stretch', 
              marginBottom: '20px' 
            }}
            className="modal-header"
            >
              <h2 style={{ 
                margin: 0, 
                color: theme.text.primary,
                fontSize: 'clamp(18px, 4vw, 20px)',
                wordBreak: 'break-word',
              }}>
                {selectedNodeData.spouse && activeTab === 'spouse' ? 'Spouse Details' : 'Person Details'}
              </h2>
              <button
                onClick={() => {
                  setSelectedNodeData(null);
                  setActiveTab('person');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: theme.text.secondary,
                  padding: '8px',
                  minWidth: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'flex-end',
                }}
              >
                ×
              </button>
            </div>

            {/* Tabs - show only if spouse exists */}
            {selectedNodeData.spouse && (selectedNodeData.spouse.name || selectedNodeData.spouse.first_name || selectedNodeData.spouse.preferred_name) && (
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginBottom: '20px', 
                borderBottom: theme.glass.border,
                flexWrap: 'wrap',
              }}>
                <button
                  onClick={() => setActiveTab('person')}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '6px 6px 0 0',
                    border: 'none',
                    borderBottom: activeTab === 'person' ? `2px solid ${theme.accent.primary || '#3B82F6'}` : '2px solid transparent',
                    background: 'transparent',
                    color: activeTab === 'person' ? theme.accent.primary || '#3B82F6' : theme.text.secondary,
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: activeTab === 'person' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Person
                </button>
                <button
                  onClick={() => setActiveTab('spouse')}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '6px 6px 0 0',
                    border: 'none',
                    borderBottom: activeTab === 'spouse' ? `2px solid ${theme.accent.primary || '#3B82F6'}` : '2px solid transparent',
                    background: 'transparent',
                    color: activeTab === 'spouse' ? theme.accent.primary || '#3B82F6' : theme.text.secondary,
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: activeTab === 'spouse' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    minHeight: '44px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                >
                  Spouse
                </button>
              </div>
            )}

            {/* Display person or spouse data based on active tab */}
            {(() => {
              const displayData = activeTab === 'spouse' && selectedNodeData.spouse ? selectedNodeData.spouse : selectedNodeData;
              
              return (
                <>
                  {displayData.picture_url && (
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <img
                        src={displayData.picture_url}
                        alt={displayData.preferred_name || displayData.first_name}
                        style={{
                          width: 'clamp(100px, 20vw, 150px)',
                          height: 'clamp(100px, 20vw, 150px)',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: theme.glass.border,
                        }}
                      />
                    </div>
                  )}

                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div>
                      <label style={{ 
                        color: theme.text.secondary, 
                        fontSize: 'clamp(13px, 2.5vw, 14px)', 
                        display: 'block', 
                        marginBottom: '4px' 
                      }}>
                        Name
                      </label>
                      <div style={{ 
                        color: theme.text.primary, 
                        fontSize: 'clamp(15px, 3vw, 16px)', 
                        fontWeight: 600,
                        wordBreak: 'break-word',
                      }}>
                        {displayData.preferred_name || displayData.first_name} {displayData.last_name || ''}
                      </div>
                    </div>

                    <div>
                      <label style={{ 
                        color: theme.text.secondary, 
                        fontSize: 'clamp(13px, 2.5vw, 14px)', 
                        display: 'block', 
                        marginBottom: '4px' 
                      }}>
                        Gender
                      </label>
                      <div style={{ 
                        color: theme.text.primary, 
                        fontSize: 'clamp(15px, 3vw, 16px)' 
                      }}>
                        {displayData.sex === 'male' ? '♂ Male' : displayData.sex === 'female' ? '♀ Female' : '⚥ Unknown'}
                      </div>
                    </div>

                    {displayData.date_of_birth && (
                      <div>
                        <label style={{ 
                          color: theme.text.secondary, 
                          fontSize: 'clamp(13px, 2.5vw, 14px)', 
                          display: 'block', 
                          marginBottom: '4px' 
                        }}>
                          Date of Birth
                        </label>
                        <div style={{ 
                          color: theme.text.primary, 
                          fontSize: 'clamp(15px, 3vw, 16px)' 
                        }}>
                          {new Date(displayData.date_of_birth).toLocaleDateString()}
                        </div>
                      </div>
                    )}

                    {displayData.date_of_death && (
                      <div>
                        <label style={{ 
                          color: theme.text.secondary, 
                          fontSize: 'clamp(13px, 2.5vw, 14px)', 
                          display: 'block', 
                          marginBottom: '4px' 
                        }}>
                          Date of Death
                        </label>
                        <div style={{ 
                          color: theme.text.primary, 
                          fontSize: 'clamp(15px, 3vw, 16px)' 
                        }}>
                          {new Date(displayData.date_of_death).toLocaleDateString()}
                        </div>
                      </div>
                    )}

                    {(displayData.city || displayData.country) && (
                      <div>
                        <label style={{ 
                          color: theme.text.secondary, 
                          fontSize: 'clamp(13px, 2.5vw, 14px)', 
                          display: 'block', 
                          marginBottom: '4px' 
                        }}>
                          Location
                        </label>
                        <div style={{ 
                          color: theme.text.primary, 
                          fontSize: 'clamp(15px, 3vw, 16px)',
                          wordBreak: 'break-word',
                        }}>
                          {[displayData.city, displayData.country].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    )}

                    {activeTab === 'person' && selectedNodeData.relationship && (
                      <div>
                        <label style={{ 
                          color: theme.text.secondary, 
                          fontSize: 'clamp(13px, 2.5vw, 14px)', 
                          display: 'block', 
                          marginBottom: '4px' 
                        }}>
                          Relationship
                        </label>
                        <div style={{ 
                          color: theme.text.primary, 
                          fontSize: 'clamp(15px, 3vw, 16px)',
                          wordBreak: 'break-word',
                        }}>
                          {selectedNodeData.relationship}
                        </div>
                      </div>
                    )}

                    {displayData.id && (
                      <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: theme.glass.border }}>
                        <button
                          onClick={() => {
                            window.location.href = `/people/${displayData.id}`;
                          }}
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            borderRadius: '8px',
                            border: theme.glass.border,
                            background: theme.accent.primary || '#3B82F6',
                            color: '#fff',
                            fontSize: 'clamp(15px, 3vw, 16px)',
                            fontWeight: 600,
                            cursor: 'pointer',
                            minHeight: '44px',
                          }}
                        >
                          View Full Profile
                        </button>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </GlassCard>
        </div>
      )}

      {!treeData && selectedPersonId && (
        <GlassCard style={{ padding: 'clamp(20px, 5vw, 24px)' }}>
          <div style={{
            padding: 'clamp(16px, 4vw, 24px)',
            textAlign: 'center',
            color: theme.text.secondary,
          }}>
            <div 
              className="spinner"
              style={{ 
                marginBottom: '16px',
                borderColor: theme.glass.border || 'rgba(255, 255, 255, 0.2)',
                borderTopColor: theme.accent.primary || '#3B82F6',
              }}
            ></div>
            <div style={{ 
              fontSize: 'clamp(15px, 3vw, 16px)', 
              fontWeight: 500, 
              marginBottom: '8px', 
              color: theme.text.primary 
            }}>
              Building family tree...
            </div>
            <div style={{ fontSize: 'clamp(13px, 2.5vw, 14px)' }}>
              Fetching relationships and constructing the tree. This may take a moment.
            </div>
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default D3TreePage;
